import { propertyImage } from "./image-paths";

export type StaySetting = "all" | "mountain" | "coast" | "river" | "heritage" | "glamp";
export type StayRegionKey = "all" | "gb" | "kpk" | "pj" | "bl";

export type StayListing = {
  slug: string;
  name: string;
  region: string;
  /** Normalized geography (shared presenter, geography-correctness upgrade
   *  Part G) — TourismRegion-led primary label ("Baltistan") and the fuller
   *  administrative line ("Skardu District · Gilgit-Baltistan, Pakistan").
   *  Undefined for mock-only listings with no DB-backed geography row;
   *  never a guessed value. */
  primaryGeography?: string | null;
  secondaryGeography?: string | null;
  /** Administrative district name alone (e.g. "Skardu"), separate from the
   *  compound secondaryGeography display string — used for structured-data
   *  mapping (addressLocality) so that mapping never has to parse display
   *  text. Undefined for mock-only listings with no DB-backed geography row. */
  district?: string | null;
  regionKey: Exclude<StayRegionKey, "all">;
  tag: string;
  type: StaySetting;
  blurb: string;
  price: string;
  priceN: number;
  /** Exact PKR Walk-In rate (Room.basePricePKR), shown as-is instead of
   *  round-tripping through the fixed USD conversion. */
  pricePkr?: number;
  /** Literal override for the listing-card price line — for package and
   *  contact-only properties whose copy must never be parsed as a USD amount. */
  cardPriceLabel?: string;
  /** Absent when nobody has set one — the site shows no rating rather than an invented one. */
  rating?: string;
  feat: number;
  image: string;
  lat: number;
  lng: number;
};

export const STAY_REGION_SLUG: Record<Exclude<StayRegionKey, "all">, string> = {
  gb: "gilgit-baltistan",
  kpk: "khyber-pakhtunkhwa",
  pj: "punjab",
  bl: "balochistan",
};

export const STAY_REGION_FILTERS: { key: StayRegionKey; label: string }[] = [
  { key: "all", label: "All Regions" },
  { key: "gb", label: "Gilgit-Baltistan" },
  { key: "kpk", label: "Khyber Pakhtunkhwa" },
  { key: "pj", label: "Punjab" },
  { key: "bl", label: "Balochistan" },
];

export const STAY_TYPE_FILTERS: { key: StaySetting; label: string }[] = [
  { key: "all", label: "All" },
  { key: "mountain", label: "Mountain" },
  { key: "coast", label: "Coast" },
  { key: "river", label: "Riverside" },
  { key: "heritage", label: "Heritage" },
  { key: "glamp", label: "Glamping" },
];

export const STAY_SORT_FILTERS = [
  { key: "featured" as const, label: "Featured" },
  { key: "priceLow" as const, label: "Price ↑" },
  { key: "rating" as const, label: "Top Rated" },
];

/** Full GreenPak catalog, matches prisma/seed.mjs (15 properties). */
export const STAYS: StayListing[] = [
  {
    slug: "greenpak-broadbeach-sonmiani",
    name: "GreenPak Broadbeach Sonmiani",
    region: "Balochistan",
    regionKey: "bl",
    tag: "Coastal",
    type: "coast",
    blurb: "A broad, near-empty beach at Sonmiani, sea, sand, and a great deal of sky.",
    price: "From $65",
    priceN: 65,
    rating: "4.8",
    feat: 1,
    image: propertyImage("greenpak-broadbeach-sonmiani"),
    lat: 25.42,
    lng: 66.59,
  },
  {
    slug: "greenpak-hotel-hunza",
    name: "GreenPak Hotel Hunza",
    region: "Gilgit-Baltistan",
    regionKey: "gb",
    tag: "Mountain",
    type: "mountain",
    blurb: "Set above the Hunza valley, with terraces that open onto Rakaposhi and the orchards below.",
    price: "From $65",
    priceN: 65,
    rating: "4.9",
    feat: 2,
    image: propertyImage("greenpak-hotel-hunza"),
    lat: 36.32,
    lng: 74.65,
  },
  {
    slug: "greenpak-resort-naltar",
    name: "GreenPak Resort Naltar",
    region: "Gilgit-Baltistan",
    regionKey: "gb",
    tag: "Alpine",
    type: "mountain",
    blurb: "A pine-forest resort in the Naltar valley, far enough from any town for a truly dark night sky.",
    price: "From $65",
    priceN: 65,
    rating: "4.7",
    feat: 3,
    image: propertyImage("greenpak-resort-naltar"),
    lat: 36.16,
    lng: 74.18,
  },
  {
    slug: "k2-hotel-skardu",
    name: "K2 Hotel by GreenPak Skardu",
    region: "Gilgit-Baltistan",
    regionKey: "gb",
    tag: "Trailhead",
    type: "mountain",
    blurb: "A base camp for the Skardu valley, the gateway to Deosai, Shigar, and the great Karakoram peaks.",
    price: "From $65",
    priceN: 65,
    rating: "4.7",
    feat: 4,
    image: propertyImage("k2-hotel-skardu"),
    lat: 35.3,
    lng: 75.63,
  },
  {
    slug: "greenpak-lagoon-gadani",
    name: "GreenPak Lagoon Gadani",
    region: "Balochistan",
    regionKey: "bl",
    tag: "Coastal",
    type: "coast",
    blurb: "On the Gadani coast, where a quiet lagoon opens onto the Arabian Sea.",
    price: "From $65",
    priceN: 65,
    rating: "4.6",
    feat: 5,
    image: propertyImage("greenpak-lagoon-gadani"),
    lat: 25.12,
    lng: 66.7,
  },
  {
    slug: "greenpak-riverfront-naran",
    name: "GreenPak Riverfront Naran",
    region: "Khyber Pakhtunkhwa",
    regionKey: "kpk",
    tag: "Riverside",
    type: "river",
    blurb: "On the bank of the Kunhar river, within reach of Saif-ul-Malook and the Babusar road.",
    price: "From $65",
    priceN: 65,
    rating: "4.5",
    feat: 6,
    image: propertyImage("greenpak-riverfront-naran"),
    lat: 34.91,
    lng: 73.65,
  },
  {
    slug: "greenpak-hotel-kalam",
    name: "GreenPak Hotel Kalam",
    region: "Khyber Pakhtunkhwa",
    regionKey: "kpk",
    tag: "Valley",
    type: "mountain",
    blurb: "At the head of the Swat valley, where the forests give way to alpine pasture.",
    price: "From $65",
    priceN: 65,
    rating: "4.6",
    feat: 7,
    image: propertyImage("greenpak-hotel-kalam"),
    lat: 35.48,
    lng: 72.58,
  },
  {
    slug: "greenpak-hotel-ayubia",
    name: "GreenPak Hotel Ayubia",
    region: "Khyber Pakhtunkhwa",
    regionKey: "kpk",
    tag: "Pine Forest",
    type: "mountain",
    blurb: "Among the pines of Ayubia, on the old pipeline walking track through the hills.",
    price: "From $65",
    priceN: 65,
    rating: "4.6",
    feat: 8,
    image: propertyImage("greenpak-hotel-ayubia"),
    lat: 34.02,
    lng: 73.4,
  },
  {
    slug: "greenpak-hotel-balakot",
    name: "GreenPak Hotel Balakot",
    region: "Khyber Pakhtunkhwa",
    regionKey: "kpk",
    tag: "Gateway",
    type: "river",
    blurb: "The first stop on the Kaghan valley road, where the mountains begin to rise.",
    price: "From $65",
    priceN: 65,
    rating: "4.5",
    feat: 9,
    image: propertyImage("greenpak-hotel-balakot"),
    lat: 34.55,
    lng: 73.35,
  },
  {
    slug: "greenpak-hotel-booni",
    name: "GreenPak Hotel Booni",
    region: "Khyber Pakhtunkhwa",
    regionKey: "kpk",
    tag: "Chitral",
    type: "mountain",
    blurb: "Upper Chitral in the Hindu Kush, a base for Shandoor polo and the Kalasha valleys.",
    price: "From $65",
    priceN: 65,
    rating: "4.6",
    feat: 10,
    image: propertyImage("greenpak-hotel-booni"),
    lat: 36.45,
    lng: 72.25,
  },
  {
    slug: "greenpak-resort-chattar-plain",
    name: "GreenPak Resort Chattar Plain",
    region: "Khyber Pakhtunkhwa",
    regionKey: "kpk",
    tag: "Meadow",
    type: "mountain",
    blurb: "A quiet resort on the Chattar plain, wide, green, and unhurried.",
    price: "From $65",
    priceN: 65,
    rating: "4.5",
    feat: 11,
    image: propertyImage("greenpak-resort-chattar-plain"),
    // The resort's verified Google listing (Sharkool, Mansehra District, on
    // the KKH); the old 34.1, 73.2 was a rough point near Abbottabad, ~58 km off.
    lat: 34.6171,
    lng: 73.1084,
  },
  {
    slug: "greenpak-resort-bansra-gali",
    name: "GreenPak Resort Bansra Gali by Highglamp",
    region: "Khyber Pakhtunkhwa",
    regionKey: "kpk",
    tag: "Glamping",
    type: "glamp",
    blurb: "Glamping in the pine forests of the Galiyat, sleep beneath a dark sky a short drive from Islamabad.",
    price: "From $65",
    priceN: 65,
    rating: "4.7",
    feat: 12,
    image: propertyImage("greenpak-resort-bansra-gali"),
    lat: 33.92,
    lng: 73.41,
  },
  {
    slug: "greenpak-hotel-nankana",
    name: "GreenPak Hotel Nankana",
    region: "Punjab",
    regionKey: "pj",
    tag: "Pilgrimage",
    type: "heritage",
    blurb: "In Nankana Sahib, serving pilgrims and travellers to one of the region's holiest sites.",
    price: "From $65",
    priceN: 65,
    rating: "4.6",
    feat: 13,
    image: propertyImage("greenpak-hotel-nankana"),
    lat: 31.45,
    lng: 73.71,
  },
  {
    slug: "greenpak-hotel-fort-munro",
    name: "GreenPak Hotel Fort Munro",
    region: "Punjab",
    regionKey: "pj",
    tag: "Hill Station",
    type: "mountain",
    blurb: "A hill-station retreat above the southern Punjab plains, cool when the lowlands burn.",
    price: "From $65",
    priceN: 65,
    rating: "4.5",
    feat: 14,
    image: propertyImage("greenpak-hotel-fort-munro"),
    lat: 29.99,
    lng: 70.1,
  },
  {
    slug: "greenpak-hotel-katas-raj",
    name: "GreenPak Hotel Katas Raj",
    region: "Punjab",
    regionKey: "pj",
    tag: "Heritage",
    type: "heritage",
    blurb: "Beside the ancient Katas Raj temple complex, a place of pools and pilgrimage.",
    price: "From $65",
    priceN: 65,
    rating: "4.6",
    feat: 15,
    image: propertyImage("greenpak-hotel-katas-raj"),
    lat: 32.72,
    lng: 72.95,
  },
];

export const FEATURED_STAY = STAYS[0];

export function getStayBySlug(slug: string) {
  return STAYS.find((s) => s.slug === slug);
}

export function listStaySlugs() {
  return STAYS.map((s) => s.slug);
}
